import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Button, Form, Modal } from "react-bootstrap";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import AdminHeader from "../../common/AdminHeader";
import Footer from "../../common/Footer";
import { baseUrl } from "../../../Utility/constants";

const initialForm = {
  id: 0,
  name: ""
};

const getErrorMessage = (error, fallbackMessage) => {
  const responseMessage = error.response?.data?.message || error.response?.data;
  return typeof responseMessage === "string"
    ? responseMessage
    : error.message || fallbackMessage;
};

const SchoolMediumMaster = () => {
  const [schoolMediums, setSchoolMediums] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState(initialForm);
  const [isSaving, setIsSaving] = useState(false);

  const fetchSchoolMediums = useCallback(async () => {
    try {
      const response = await axios.get(`${baseUrl}/api/SchoolMediumMaster`);
      if (!Array.isArray(response.data)) {
        throw new Error("The School Medium API returned an invalid response.");
      }
      setSchoolMediums(
        response.data.filter((schoolMedium) => Number(schoolMedium.isActive) === 1)
      );
    } catch (error) {
      console.error("Failed to fetch school mediums:", error);
      toast.error(getErrorMessage(error, "Failed to fetch school mediums."));
    }
  }, []);

  useEffect(() => {
    fetchSchoolMediums();
  }, [fetchSchoolMediums]);

  const handleAdd = () => {
    setFormData({ ...initialForm });
    setEditMode(false);
    setShowModal(true);
  };

  const handleEdit = (schoolMedium) => {
    setFormData({
      id: schoolMedium.id,
      name: schoolMedium.name || ""
    });
    setEditMode(true);
    setShowModal(true);
  };

  const handleClose = () => {
    if (isSaving) {
      return;
    }
    setShowModal(false);
    setFormData({ ...initialForm });
    setEditMode(false);
  };

  const handleSave = async (event) => {
    event.preventDefault();
    const name = formData.name.trim();
    if (!name) {
      toast.warning("Enter a school medium name.");
      return;
    }

    setIsSaving(true);
    try {
      const payload = { id: formData.id, name, isActive: 1 };
      if (editMode) {
        await axios.put(
          `${baseUrl}/api/SchoolMediumMaster/${formData.id}`,
          payload
        );
        toast.success("School medium updated successfully.");
      } else {
        await axios.post(`${baseUrl}/api/SchoolMediumMaster`, payload);
        toast.success("School medium added successfully.");
      }
      setShowModal(false);
      setFormData({ ...initialForm });
      setEditMode(false);
      await fetchSchoolMediums();
    } catch (error) {
      console.error("Failed to save school medium:", error);
      toast.error(getErrorMessage(error, "Failed to save school medium."));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (schoolMedium) => {
    if (!window.confirm(`Delete the "${schoolMedium.name}" school medium?`)) {
      return;
    }

    try {
      await axios.delete(
        `${baseUrl}/api/SchoolMediumMaster/${schoolMedium.id}`
      );
      toast.success("School medium deleted successfully.");
      await fetchSchoolMediums();
    } catch (error) {
      console.error("Failed to delete school medium:", error);
      toast.error(getErrorMessage(error, "Failed to delete school medium."));
    }
  };

  return (
    <>
      <AdminHeader />
      <main className="container mt-5 mb-5">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h2 className="mb-0">School Medium</h2>
          <Button variant="primary" onClick={handleAdd}>
            Add School Medium
          </Button>
        </div>

        <div className="table-responsive">
          <table className="table table-bordered table-striped">
            <thead className="table-dark">
              <tr>
                <th scope="col">#</th>
                <th scope="col">Medium Name</th>
                <th scope="col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {schoolMediums.length === 0 ? (
                <tr>
                  <td colSpan="3" className="text-center">
                    No school mediums found.
                  </td>
                </tr>
              ) : (
                schoolMediums.map((schoolMedium, index) => (
                  <tr key={schoolMedium.id}>
                    <td>{index + 1}</td>
                    <td>{schoolMedium.name}</td>
                    <td>
                      <Button
                        className="me-2"
                        variant="warning"
                        onClick={() => handleEdit(schoolMedium)}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="danger"
                        onClick={() => handleDelete(schoolMedium)}
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </main>

      <Modal show={showModal} onHide={handleClose}>
        <Form onSubmit={handleSave}>
          <Modal.Header closeButton>
            <Modal.Title>
              {editMode ? "Edit School Medium" : "Add School Medium"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-3" controlId="schoolMediumName">
              <Form.Label>Medium Name</Form.Label>
              <Form.Control
                autoFocus
                required
                type="text"
                value={formData.name}
                onChange={(event) =>
                  setFormData({ ...formData, name: event.target.value })
                }
                placeholder="Enter medium name"
                maxLength={100}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={handleClose}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={isSaving}>
              {isSaving ? "Saving..." : editMode ? "Update" : "Add"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <ToastContainer />
      <Footer />
    </>
  );
};

export default SchoolMediumMaster;
